import clsx from 'clsx';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import TextArea from 'components/Form/TextArea';
import Icon from 'components/Icon';
import Layout from 'components/Layout';
import Modal from 'components/Modal';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {FormFields} from 'types/utils';
import {onApiFormError} from 'utils/request/result';

import {
    AcceptOrderFormData,
    ClientInboxDocument,
    usePostAcceptOrder,
} from '../../services';
import {AcceptOrderState} from '../types';

type FormData = FormFields<AcceptOrderFormData>;

export type SignOffDocumentModalProps = {
    document: ClientInboxDocument;
    isOpen: boolean;
    onClose: () => void;
    portalMid: string;
    setState: (value: AcceptOrderState) => void;
};

const SignOffDocumentModal: FC<SignOffDocumentModalProps> = ({
    document,
    isOpen,
    onClose,
    portalMid,
    setState,
}) => {
    const {t} = useTranslation('Portal');
    const type =
        document.documentType === 'acceptance' ? 'confirmation' : 'order';
    const acceptOrder = usePostAcceptOrder();
    const [signerName, setSignerName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const methods = useForm<FormData>();
    const {handleSubmit} = methods;

    const onSignOff = ({note, signer}: FormData) => {
        const formData = {
            note,
            signer,
        };
        acceptOrder
            .mutateAsync({documentMid: document.mid, formData, portalMid})
            .then(() => {
                setSignerName(signer);
                setState('accepted');
                setTimeout(onClose, 2000);
            })
            .catch(() => {
                onApiFormError(methods);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} preventOutsideClick={false}>
            <FormProvider {...methods}>
                <Modal.Header>
                    {t(`signOffDocumentModal.${type}.header`)}
                </Modal.Header>
                <Modal.Body className="h-full">
                    <Layout.Stacked className="mt-6">
                        <InputText
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={true}
                            label={t(
                                `signOffDocumentModal.${type}.signerInput`,
                            )}
                            maxLength={100}
                            name="signer"
                            required={true}
                        />
                        <TextArea
                            label={t('signOffDocumentModal.noteInput')}
                            name="note"
                            required={false}
                            rows={6}
                        />
                    </Layout.Stacked>
                    <div
                        className={clsx(
                            signerName ? 'signature' : 'hidden',
                            'pt-4',
                        )}
                    >
                        <div>{signerName}</div>
                        <div className="mx-2 my-auto">
                            <Icon className="text-coral-800" icon="success" />
                        </div>
                    </div>
                    <p className="my-4">
                        {t(`signOffDocumentModal.${type}.info`)}
                    </p>
                    <FormResult />
                </Modal.Body>
                <Modal.Footer>
                    <Modal.Button
                        kind="secondary"
                        onClick={onClose}
                        size="sm"
                        type="cancel"
                    >
                        {t('signOffDocumentModal.cancel')}
                    </Modal.Button>
                    <Modal.Button
                        className="ml-2"
                        isLoading={isLoading}
                        kind="primary"
                        onClick={handleSubmit((formData) => {
                            onSignOff(formData);
                            setIsLoading(true);
                        })}
                        size="sm"
                        type="submit"
                    >
                        {t(`signOffDocumentModal.${type}.signOff`)}
                    </Modal.Button>
                </Modal.Footer>
            </FormProvider>
        </Modal>
    );
};

export default SignOffDocumentModal;
